/**
 * The Flagsmith flags of one BU's environment.
 *
 * Item privileges are flag names, and each BU has its own Flagsmith environment, so the list has
 * to come from that BU's key rather than from the panel's own environment. Flagsmith's client API
 * answers `GET {apiUrl}/flags/` for whichever environment key is sent in the header — that key is
 * a public client key, which is why it can be read straight from the browser.
 *
 * Without a key the list is empty and the UI points at the BU instead of offering a free-text
 * field: a flag typed by hand that does not exist silently hides the item from everyone.
 *
 * @module Pages/BUs/WhatsNew
 */

import { useQuery } from '@tanstack/react-query';
import { buildFlagsUrl } from '../Utils/buildFlagsUrl';
import { env } from '@/config/env';

/** One flag, as the privileges picker lists it. */
export interface TenantFlag {
  name: string;
  enabled: boolean;
  description?: string;
}

interface FlagsmithFlag {
  enabled: boolean;
  feature: { name: string; description?: string | null };
}

export interface UseTenantFlagsResult {
  flags: TenantFlag[];
  isLoading: boolean;
  /** True when the BU has no environment key — the list cannot be fetched at all. */
  isMissingKey: boolean;
  error: string | null;
  refetch: () => void;
}

export const useTenantFlags = (environmentKey?: string | null): UseTenantFlagsResult => {
  const isMissingKey = !environmentKey;

  const query = useQuery({
    queryKey: ['flagsmith', 'flags', environmentKey],
    enabled: !isMissingKey && Boolean(env.flagsmith.apiUrl),
    // Flags change rarely and the picker reopens often.
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<TenantFlag[]> => {
      const response = await fetch(buildFlagsUrl(env.flagsmith.apiUrl), {
        headers: { 'X-Environment-Key': environmentKey ?? '' },
      });

      if (!response.ok) {
        throw new Error('Não foi possível carregar as flags dessa BU.');
      }

      /*
        Checked rather than assumed: a wrong path on this host answers 200 with the HTML of the
        Flagsmith web app, so `response.ok` is not enough to know the answer is a flags list.
      */
      const data: unknown = await response.json().catch(() => undefined);

      if (!Array.isArray(data)) {
        throw new Error('A resposta do Flagsmith não foi uma lista de flags. Confira a API URL.');
      }

      return (data as FlagsmithFlag[])
        .map(flag => ({
          name: flag.feature.name,
          enabled: flag.enabled,
          description: flag.feature.description ?? undefined,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  return {
    flags: query.data ?? [],
    isLoading: query.isLoading,
    isMissingKey,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: () => {
      void query.refetch();
    },
  };
};
